import React, { useState } from 'react';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Calendar as CalendarIcon, Users, Minus, Plus } from 'lucide-react';
import { format } from 'date-fns';
import { pt } from 'date-fns/locale';
import { useToast } from '../hooks/use-toast';
import { useTranslation } from 'react-i18next';

const Booking = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [formData, setFormData] = useState({ name: '', email: '', phone: '' });
  const [checkIn, setCheckIn] = useState(null);
  const [checkOut, setCheckOut] = useState(null);
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);
  const [babies, setBabies] = useState(0);
  const [isGuestPopoverOpen, setIsGuestPopoverOpen] = useState(false);

  const maxAdultsChildren = 6;
  const maxBabies = 5;
  const totalAdultsChildren = adults + children;

  const canIncreaseAdults = totalAdultsChildren < maxAdultsChildren;
  const canIncreaseChildren = totalAdultsChildren < maxAdultsChildren;
  const canIncreaseBabies = babies < maxBabies;

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!checkIn || !checkOut) {
      toast({
        title: t('booking.toast_attention'),
        description: t('booking.toast_select_dates'),
        variant: 'destructive'
      });
      return;
    }

    const message = `${t('booking.whatsapp_greeting')}

${t('booking.name')}: ${formData.name}
${t('booking.email')}: ${formData.email}
${t('booking.phone')}: ${formData.phone}

${t('booking.check_in')}: ${format(checkIn, 'dd/MM/yyyy', { locale: pt })}
${t('booking.check_out')}: ${format(checkOut, 'dd/MM/yyyy', { locale: pt })}

${t('booking.guests')}:
- ${t('booking.adults')}: ${adults}
- ${t('booking.children')}: ${children}
- ${t('booking.babies')}: ${babies}`;

    const whatsappUrl = `https://wa.me/558199773987?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');

    toast({
      title: t('booking.toast_redirecting'),
      description: t('booking.toast_redirecting_desc')
    });
  };

  const guestSummary = () => {
    const parts = [];
    if (adults > 0) parts.push(t('booking.adult_plural', { count: adults }));
    if (children > 0) parts.push(t('booking.child_plural', { count: children }));
    if (babies > 0) parts.push(t('booking.baby_plural', { count: babies }));
    return parts.join(', ') || t('booking.select_guests');
  };

  return (
    <section id="booking" className="py-24 bg-[#F7F6F3]">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-playfair font-bold text-[#2A3C48] mb-6">
            {t('booking.title')}
          </h2>
          <p className="text-lg text-gray-700 max-w-2xl mx-auto">
            {t('booking.subtitle')}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl p-8">
          {/* Personal Info */}
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div>
              <Label htmlFor="name">{t('booking.name')} *</Label>
              <Input
                id="name"
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="mt-2 h-12"
                placeholder={t('booking.name_placeholder')}
              />
            </div>
            <div>
              <Label htmlFor="email">{t('booking.email')} *</Label>
              <Input
                id="email"
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="mt-2 h-12"
                placeholder={t('booking.email_placeholder')}
              />
            </div>
          </div>

          <div className="mb-6">
            <Label htmlFor="phone">{t('booking.phone')} *</Label>
            <Input
              id="phone"
              type="tel"
              required
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="mt-2 h-12"
              placeholder={t('booking.phone_placeholder')}
            />
          </div>

          {/* Dates and Guests */}
          <div className="grid md:grid-cols-3 gap-4 mb-6">
            {/* Check-in */}
            <div>
              <Label>{t('booking.check_in')} *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal h-12 mt-2 border-2 hover:border-[#335B63] transition-colors"
                  >
                    <CalendarIcon className="mr-2 h-5 w-5 text-[#335B63]" />
                    <span>{checkIn ? format(checkIn, 'dd/MM/yyyy', { locale: pt }) : t('booking.select')}</span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={checkIn}
                    onSelect={setCheckIn}
                    disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Check-out */}
            <div>
              <Label>{t('booking.check_out')} *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal h-12 mt-2 border-2 hover:border-[#335B63] transition-colors"
                  >
                    <CalendarIcon className="mr-2 h-5 w-5 text-[#335B63]" />
                    <span>{checkOut ? format(checkOut, 'dd/MM/yyyy', { locale: pt }) : t('booking.select')}</span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={checkOut}
                    onSelect={setCheckOut}
                    disabled={(date) => {
                      const today = new Date(new Date().setHours(0, 0, 0, 0));
                      return date < today || (checkIn && date <= checkIn);
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Guests */}
            <div>
              <Label>{t('booking.guests')} *</Label>
              <Popover open={isGuestPopoverOpen} onOpenChange={setIsGuestPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal h-12 mt-2 border-2 hover:border-[#335B63] transition-colors"
                  >
                    <Users className="mr-2 h-5 w-5 text-[#335B63]" />
                    <span className="truncate">{guestSummary()}</span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80" align="start">
                  <div className="space-y-4">
                    {/* Adults */}
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{t('booking.adults')}</p>
                        <p className="text-xs text-gray-500">{t('booking.adults_age')}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          className="h-8 w-8 rounded-full"
                          onClick={() => setAdults(Math.max(1, adults - 1))}
                          disabled={adults <= 1}
                        >
                          <Minus className="h-4 w-4" />
                        </Button>
                        <span className="w-8 text-center font-medium">{adults}</span>
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          className="h-8 w-8 rounded-full"
                          onClick={() => setAdults(adults + 1)}
                          disabled={!canIncreaseAdults}
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    {/* Children */}
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{t('booking.children')}</p>
                        <p className="text-xs text-gray-500">{t('booking.children_age')}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          className="h-8 w-8 rounded-full"
                          onClick={() => setChildren(Math.max(0, children - 1))}
                          disabled={children <= 0}
                        >
                          <Minus className="h-4 w-4" />
                        </Button>
                        <span className="w-8 text-center font-medium">{children}</span>
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          className="h-8 w-8 rounded-full"
                          onClick={() => setChildren(children + 1)}
                          disabled={!canIncreaseChildren}
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    {/* Babies */}
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{t('booking.babies')}</p>
                        <p className="text-xs text-gray-500">{t('booking.babies_age')}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          className="h-8 w-8 rounded-full"
                          onClick={() => setBabies(Math.max(0, babies - 1))}
                          disabled={babies <= 0}
                        >
                          <Minus className="h-4 w-4" />
                        </Button>
                        <span className="w-8 text-center font-medium">{babies}</span>
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          className="h-8 w-8 rounded-full"
                          onClick={() => setBabies(babies + 1)}
                          disabled={!canIncreaseBabies}
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    <div className="pt-4 border-t text-xs text-gray-600">
                      <p>• {t('booking.guests_max')}</p>
                      <p>• {t('booking.babies_max')}</p>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <Button
            type="submit"
            className="w-full h-14 bg-[#F2B705] hover:bg-[#d9a504] text-[#2A3C48] font-bold text-sm lg:text-lg rounded-full transition-all"
          >
            {t('booking.button')}
          </Button>

          <p className="text-center text-sm text-gray-600 mt-4">
            {t('booking.response_time')}
          </p>
        </form>
      </div>
    </section>
  );
};

export default Booking;
